<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>
                <!-- Left/top Side: Ads -->
                <v-flex v-if="!isMobile" xs12 sm6 class="ads-section">
                    <div class="ads-bg">
                        <public-notifications></public-notifications>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout fill-height align-center justify-center>
                        <div class="login-container">
                            <v-card  class="mx-auto login-card" max-width="450">
                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                <h3 class="text-xs-center">OTP Verification</h3>
                                
                                <v-card-text>
                                    <v-alert v-if="error" :value="true" color="warning" icon="warning" outline>
                                        {{errorMsg}}
                                    </v-alert>  
                                    <v-alert v-if="info" :value="true" color="info" icon="info" outline>
                                        {{infoMsg}}
                                    </v-alert> 
                                    <v-alert v-if="this.$store.state.newDevice" :value="true" color="warning" icon="warning" outline>
                                        Login from new device detected.
                                    </v-alert>  

                                    <p style="margin-top:20px;">Please check the OTP sent to your registered email.</p>
                                    <v-text-field
                                        ref="otpField"
                                        style="margin-top:20px;"
                                        solo
                                        prepend-inner-icon="lock"
                                        v-model="otp"
                                        label="Enter OTP"
                                        :rules="[rules.required, rules.min]"
                                        type="text"
                                        :disabled="disabled"
                                    ></v-text-field>
                                    <span v-if="hide_regenerate" class="warning--text"><b>{{expirationCounter}}</b> seconds remaining until expiration</span>
                                </v-card-text>
                                <v-divider></v-divider>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn :color="welcomeButtonColor" type="submit" :loading="loading" dark @click="mode='validate'" style="margin-right:5px;">Verify</v-btn>
                                    <v-btn type="submit" :loading="loading_generate" v-if="!hide_regenerate" @click="mode='regenerate'" style="margin-right:10px;">Regenerate</v-btn>
                                </v-card-actions>
                                </v-form>  
                            </v-card>
                        </div>
                        <div class="footer-section caption">
                            <div class="white--text text-xs-center">&copy; Copyright 2024 SweldoNow OPC. All Rights Reserved.</div>
                            <other-legal-docs v-if="!isMobile"></other-legal-docs>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>
    </v-app>
</template>

<script>

import '@/assets/css/public.css'
import '@/assets/css/season.css'
import auth from "@/auth/auth.js";
import Recaptcha from "@/components/ReCaptcha.vue";
import securityApi from "@/api/security.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Sign In With Google, Continue with Facebook, Sign In With Email' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        Recaptcha,
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'public-notifications': () => import('@/components/Common/PublicNotifications'),
    },
    data () {
        return {
            error: false,
            errorMsg: null,
            info: false,
            infoMsg: null,
            loading: false,
            loading_generate: false,
            disabled: false,
            hide_regenerate: true,
            generateTimer: null,
            otp_timer: 0,
            otp_expiration: 300000, //5mins (300000ms), 1sec=1000ms
            otp: '',
            recaptchResponse: null,
            seasonDetails: publicPages.getSeasonDetails(),
            rules: {
                required: value => !!value || "Please enter the OTP sent to your email",
                min: v => v.length == 6 || "Should be 6 characters"
            },
            ua_parser: null,
            ua_data: null
        }
    },
    methods: {
        executeRecaptcha() {
            if (this.mode == 'validate') {
            if (this.$refs.form.validate()) {
                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
            }
            } else {
            this.$refs.recaptcha.execute()
            }
        },
        submitReCaptcha(response) {
            this.recaptchResponse = response
            if (this.mode == 'validate') {
                this.validate()
            } else {
                this.regenerate()
            }
        },
        async validate() {
            try {
                this.loading = true
                this.disabled = true

                //possible responses: 
                //401 invalid userid/password or invalid recaptcha response
                //200 valid otp
                //409 expired or invalid otp

                var data = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    otp: this.otp,
                    recaptchaResponse: this.recaptchResponse
                }

                const response = await securityApi.verifyOTP(data)

                //valid otp, relogin then redirect to login page
                if (response.status == 200) { 
                    const userAgent = await getUAData()
                    const loginData = {
                        email: this.$store.state.email,
                        password: this.$store.state.pwd,
                        recaptchaResponse: this.recaptchResponse,

                        browserName: userAgent.browser.name,
                        deviceModel: userAgent.device.model,
                        deviceType: userAgent.device.type,
                        deviceVendor: userAgent.device.vendor,
                        oSName: userAgent.os.name,
                        oSVersion: userAgent.os.version,
                        deviceFingerprint: userAgent.fingerprint,

                        sourceType: 'Customer'
                    }

                    await auth.login(loginData, true)

                    if (!auth.loggedIn()) {
                        this.error = true
                        this.errorMsg = "Error logging in"
                    }else{
                        auth.clearLoginDetails()
                        if (auth.onBoarded()) this.$router.push("/")
                        else this.$router.push("/features")
                    }
                }
            }catch(error){
                this.error = true
                this.loading = false
                this.disabled = false

                if (error.toString().includes("409")) {
                    //invlid or expired otp
                    this.errorMsg = "Invalid or expired OTP."

                    this.hide_regenerate = false
                    clearInterval(this.generateTimer)
                }            
                else if (error.toString().includes("401")) {
                    //not authorized to perform this action
                    this.errorMsg = "You are not authorized to perform this action "
                    this.hide_regenerate = true
                }
                else
                {
                    this.errorMsg = error
                }
            }
        },
        async regenerate() {
            try {
                this.clearError()
                
                this.disabled = true
                this.loading_generate = true

                //generate otp then send to email
                var data = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    recaptchaResponse: this.recaptchResponse
                }
                const response = await securityApi.generateOTP(data)
                if (response.status == 200) {
                    this.info = true
                    this.infoMsg = "New OTP has been created and sent to your email."
                    this.$refs.otpField.focus()
                }

                //enable again regenerate button after 5mins
                this.generateTimer = setInterval(this.enableRegenerate, 1000)

                this.disabled = false
                this.loading_generate = false
                this.hide_regenerate = true //disable regenerate button until 5mins expiration
            }catch(error){
                this.error = true
                this.errorMsg = error
            }
        },
        enableRegenerate() {
            this.otp_timer = this.otp_timer + 1000

            //enable regenerate button after 5mins (300000ms), 1sec=1000ms
            if (parseInt(this.otp_timer)>parseInt(this.otp_expiration)) {
                this.hide_regenerate = false    
                clearInterval(this.generateTimer)
                this.otp_timer = 0       
            }
        },
        clearError() {
            this.error = false
            this.errorMsg = null
            this.otp = ''
        }
    },
    mounted() {
        // Focus the text field when component is mounted
        this.$nextTick(() => {
            if (this.$refs.otpField) {
                this.$refs.otpField.focus();
            }
        });
    },
    created () {
        if (auth.loggedIn()) 
            this.$router.push("/");
        else
        {
          //security if manually accessed this page
            if (this.$store.state.email=='' || this.$store.state.pwd=='') {
                this.$router.push("/login");
            }

            this.generateTimer = setInterval(this.enableRegenerate, 1000)
        }
    },
    destroyed() {
        clearInterval(this.generateTimer)
    },
    computed: {
        isMobile() {
            return window.innerWidth < 600
        },
        expirationCounter() {
            return (this.otp_expiration-this.otp_timer)/1000
        },
        welcomeSectionClass() {
            return this.isMobile ? this.seasonDetails.classNameMobile : this.seasonDetails.className
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor
        }
    }
}
</script>